import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchWorkflowRouteActors } from "../../contents/redux/actions/workflowRouteActions";

const RejectMembershipRequestModal = ({
  open,
  onClose,
  onSubmit,
  workflowId,
  requester,
  requesterId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [commentText, setCommentText] = useState("");
  const [selectedOption, setSelectedOption] = useState("");

  // Access Redux state
  const workflowRouteActors = useSelector(
    (state) => state.workflow?.workflowRouteActors || []
  );

  // Fetch workflow actors when workflowId changes
  useEffect(() => {
    if (workflowId) {
      dispatch(fetchWorkflowRouteActors(workflowId));
    }
  }, [workflowId, dispatch]);

  // Build dropdown options with requester as first option
  const dropdownOptions = [
    {
      value: `requester-${requesterId || "default"}`,
      label: `${requester || "Requester"} (Original Requester)`,
      roleId: requesterId || null,
    },
    ...workflowRouteActors.map((actor, index) => ({
      value: `${actor.roleId}-${index}`,
      label: actor.roleName,
      roleId: actor.roleId,
    })),
  ];

  // Set default selected option when options change
  useEffect(() => {
    if (dropdownOptions.length > 0 && !selectedOption) {
      setSelectedOption(dropdownOptions[0].value);
    }
  }, [dropdownOptions, selectedOption]);

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const handleSubmit = () => {
    if (!commentText.trim()) {
      alert("Reason for rejection is required.");
      return;
    }

    const selectedActor = dropdownOptions.find(
      (option) => option.value === selectedOption
    );

    const formattedCommentText = `[Reject to ${selectedActor.label}]: ${commentText}`;

    // Pass formatted commentText to parent
    onSubmit({ commentText: formattedCommentText });

    // Reset and close modal
    setCommentText("");
    setSelectedOption(dropdownOptions[0]?.value || "");
    onClose();

    navigate("/membershipapprovals"); // route back after rejection
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reject Membership Request</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Reason for Rejection"
          fullWidth
          multiline
          rows={4}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          required
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth>
          <InputLabel id="reject-recipient-label">Reject To</InputLabel>
          <Select
            labelId="reject-recipient-label"
            value={selectedOption}
            label="Reject To"
            onChange={handleOptionChange}
          >
            {dropdownOptions.length > 0 ? (
              dropdownOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No recipients available</MenuItem>
            )}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color="error"
          variant="contained"
          disabled={!commentText.trim() || !selectedOption}
        >
          Reject
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejectMembershipRequestModal;
